import type { SVGProps } from 'react';
import {
  ArrowDownUp,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Bell,
  BookOpen,
  Building,
  Building2,
  Calendar,
  CheckCheck,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Circle,
  CircleCheck,
  CircleQuestionMark,
  Clock,
  Copy,
  Database,
  Download,
  ExternalLink,
  Eye,
  FilePenLine,
  FileText,
  GitMerge,
  Globe,
  History,
  House,
  Image,
  Info,
  KeyRound,
  LayoutGrid,
  Link,
  ListFilter,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Megaphone,
  Menu,
  MousePointerClick,
  Network,
  PanelRight,
  Pause,
  Pencil,
  Phone,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Square,
  SquareCheckBig,
  Tag,
  Target,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Upload,
  User,
  Users,
  Wallet,
  X,
  List,
  type LucideIcon,
} from 'lucide-react';

/**
 * Icon aliases → Lucide geometry (design system §2). The 47 aliases of the HP prototype come
 * first, verbatim; the aliases below the divider are Back Office additions. Components call icons
 * by alias so a geometry swap never touches call sites. The SVG carries `data-lucide` with the
 * canonical Lucide name, exactly like the prototype's icon registry.
 */
const ICONS = {
  building: ['building-2', Building2],
  projects: ['building', Building],
  home: ['house', House],
  search: ['search', Search],
  arrowRight: ['arrow-right', ArrowRight],
  arrowUpRight: ['arrow-up-right', ArrowUpRight],
  chevronRight: ['chevron-right', ChevronRight],
  chevronLeft: ['chevron-left', ChevronLeft],
  chevronDown: ['chevron-down', ChevronDown],
  arrowLeft: ['arrow-left', ArrowLeft],
  plus: ['plus', Plus],
  close: ['x', X],
  panel: ['panel-right', PanelRight],
  grid: ['layout-grid', LayoutGrid],
  users: ['users', Users],
  team: ['network', Network],
  lock: ['lock', Lock],
  megaphone: ['megaphone', Megaphone],
  wallet: ['wallet', Wallet],
  settings: ['settings', Settings],
  book: ['book-open', BookOpen],
  clock: ['clock', Clock],
  checkSquare: ['square-check-big', SquareCheckBig],
  check: ['check', Check],
  checkCheck: ['check-check', CheckCheck],
  circleCheck: ['circle-check', CircleCheck],
  circle: ['circle', Circle],
  globe: ['globe', Globe],
  draft: ['file-pen-line', FilePenLine],
  download: ['download', Download],
  upload: ['upload', Upload],
  sort: ['arrow-down-up', ArrowDownUp],
  filter: ['list-filter', ListFilter],
  sliders: ['sliders-horizontal', SlidersHorizontal],
  help: ['circle-question-mark', CircleQuestionMark],
  info: ['info', Info],
  eye: ['eye', Eye],
  edit: ['pencil', Pencil],
  menu: ['menu', Menu],
  link: ['link', Link],
  pin: ['map-pin', MapPin],
  file: ['file-text', FileText],
  spark: ['sparkles', Sparkles],
  rotateCcw: ['rotate-ccw', RotateCcw],
  trendingUp: ['trending-up', TrendingUp],
  target: ['target', Target],
  badgeCheck: ['badge-check', BadgeCheck],
  mousePointerClick: ['mouse-pointer-click', MousePointerClick],
  // --- Back Office additions (same Lucide geometry source) ---
  logout: ['log-out', LogOut],
  database: ['database', Database],
  refresh: ['refresh-cw', RefreshCw],
  merge: ['git-merge', GitMerge],
  shield: ['shield-check', ShieldCheck],
  alert: ['triangle-alert', TriangleAlert],
  user: ['user', User],
  history: ['history', History],
  copy: ['copy', Copy],
  external: ['external-link', ExternalLink],
  play: ['play', Play],
  pause: ['pause', Pause],
  stop: ['square', Square],
  trash: ['trash-2', Trash2],
  image: ['image', Image],
  mail: ['mail', Mail],
  phone: ['phone', Phone],
  chart: ['chart-column', ChartColumn],
  tag: ['tag', Tag],
  audit: ['scroll-text', ScrollText],
  key: ['key-round', KeyRound],
  calendar: ['calendar', Calendar],
  bell: ['bell', Bell],
  list: ['list', List],
} as const satisfies Record<string, readonly [string, LucideIcon]>;

export type IconName = keyof typeof ICONS;

export const ICON_NAMES = Object.keys(ICONS) as IconName[];

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name' | 'ref'> {
  name: IconName;
}

function render(name: IconName, className: string, rest: Omit<IconProps, 'name'>) {
  const [canonical, Component] = ICONS[name];
  return (
    <Component
      className={className}
      data-lucide={canonical}
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
      {...rest}
    />
  );
}

/** `svg.icon` — the default 19 px icon (the prototype's `icon()`); the parent class sets the size. */
export function Icon({ name, className, ...rest }: IconProps) {
  return render(name, className ? `icon ${className}` : 'icon', rest);
}

/** `svg.hp-icon` — 18 px system icon (the prototype's `HPUI.icon()`). */
export function HpIcon({ name, className, ...rest }: IconProps) {
  return render(name, className ? `hp-icon ${className}` : 'hp-icon', rest);
}

/** `span[data-icon] > svg.icon` — the shell's static icon slot that `[data-icon]` selectors target. */
export function IconSlot({ name, className, ...rest }: IconProps) {
  return (
    <span data-icon={name} className={className}>
      {render(name, 'icon', rest)}
    </span>
  );
}
